import { useEffect } from 'react'
import { useSolitaireStore } from './stores/solitaireStore'
import type { CardSource } from './stores/solitaireStore'
import { isRed, getSuitSymbol } from './utils/cardUtils'
import type { Card } from './utils/cardUtils'
import './Solitaire.css'

export default function Solitaire() {
  const {
    tableau,
    foundations,
    stock,
    waste,
    gameStatus,
    moves,
    selectedCard,
    newGame,
    drawCard,
    selectCard,
    moveToFoundation,
    moveToTableau,
    clearSelection,
    tryAutoMoveToFoundation,
  } = useSolitaireStore()

  // Initialize game on first mount if no existing game
  useEffect(() => {
    const hasCards = stock.length > 0 || tableau.some(col => col.length > 0)
    if (!hasCards) {
      newGame()
    }
  }, [])

  const isCardSelected = (source: CardSource, sourceIndex: number, cardIndex: number) => {
    if (!selectedCard) return false
    return (
      selectedCard.source === source &&
      selectedCard.sourceIndex === sourceIndex &&
      selectedCard.cardIndex === cardIndex
    )
  }

  const isCardInSelectedStack = (source: CardSource, sourceIndex: number, cardIndex: number) => {
    if (!selectedCard || selectedCard.source !== 'tableau') return false
    return (
      source === 'tableau' &&
      selectedCard.sourceIndex === sourceIndex &&
      cardIndex >= selectedCard.cardIndex
    )
  }

  const handleCardClick = (
    source: CardSource,
    sourceIndex: number,
    cardIndex: number,
    card: Card
  ) => {
    if (gameStatus !== 'playing') return
    if (!card.faceUp) return

    if (selectedCard) {
      // Try to move to this location
      if (source === 'tableau') {
        moveToTableau(sourceIndex)
      } else if (source === 'foundation') {
        moveToFoundation(sourceIndex)
      } else {
        // Clicking waste when something is selected - select the new card instead
        selectCard(source, sourceIndex, cardIndex)
      }
    } else {
      selectCard(source, sourceIndex, cardIndex)
    }
  }

  const handleCardDoubleClick = (source: CardSource, sourceIndex: number) => {
    if (gameStatus !== 'playing') return
    tryAutoMoveToFoundation(source, sourceIndex)
  }

  const handleEmptyTableauClick = (columnIndex: number) => {
    if (gameStatus !== 'playing') return
    if (selectedCard) {
      moveToTableau(columnIndex)
    }
  }

  const handleEmptyFoundationClick = (foundationIndex: number) => {
    if (gameStatus !== 'playing') return
    if (selectedCard) {
      moveToFoundation(foundationIndex)
    }
  }

  const handleStockClick = () => {
    if (gameStatus !== 'playing') return
    clearSelection()
    drawCard()
  }

  const renderCard = (
    card: Card,
    source: CardSource,
    sourceIndex: number,
    cardIndex: number,
    isTop: boolean = false
  ) => {
    const colorClass = isRed(card.suit) ? 'red' : 'black'
    const selected = isCardSelected(source, sourceIndex, cardIndex)
    const inStack = isCardInSelectedStack(source, sourceIndex, cardIndex)

    return (
      <div
        key={card.id}
        className={`solitaire-card ${card.faceUp ? 'face-up' : 'face-down'} ${colorClass} ${selected || inStack ? 'selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          if (card.faceUp) {
            handleCardClick(source, sourceIndex, cardIndex, card)
          }
        }}
        onDoubleClick={(e) => {
          e.stopPropagation()
          if (card.faceUp && isTop) {
            handleCardDoubleClick(source, sourceIndex)
          }
        }}
      >
        {card.faceUp ? (
          <>
            <span className="card-corner top-left">
              <span className="card-rank">{card.rank}</span>
              <span className="card-suit">{getSuitSymbol(card.suit)}</span>
            </span>
            <span className="card-center">{getSuitSymbol(card.suit)}</span>
            <span className="card-corner bottom-right">
              <span className="card-rank">{card.rank}</span>
              <span className="card-suit">{getSuitSymbol(card.suit)}</span>
            </span>
          </>
        ) : (
          <div className="card-back" />
        )}
      </div>
    )
  }

  const renderFoundationPlaceholder = (index: number) => {
    const suits = ['♠', '♥', '♦', '♣']
    return (
      <div
        className="solitaire-card placeholder foundation-placeholder"
        onClick={() => handleEmptyFoundationClick(index)}
      >
        <span className="placeholder-suit">{suits[index]}</span>
      </div>
    )
  }

  return (
    <div className="solitaire-app" onClick={clearSelection}>
      {/* Header */}
      <div className="solitaire-header">
        <button className="solitaire-new-game" onClick={(e) => { e.stopPropagation(); newGame(); }}>
          New Game
        </button>
        <span className="solitaire-moves">Moves: {moves}</span>
        {gameStatus === 'won' && <span className="solitaire-win">YOU WIN!</span>}
      </div>

      {/* Top row: Stock, Waste, spacer, Foundations */}
      <div className="solitaire-top-row">
        {/* Stock pile */}
        <div
          className={`solitaire-stock ${stock.length === 0 ? 'empty' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleStockClick(); }}
        >
          {stock.length > 0 ? (
            <div className="solitaire-card face-down">
              <div className="card-back" />
            </div>
          ) : (
            <div className="solitaire-card placeholder stock-placeholder">
              <span className="recycle-icon">&#8635;</span>
            </div>
          )}
        </div>

        {/* Waste pile */}
        <div className="solitaire-waste" onClick={(e) => e.stopPropagation()}>
          {waste.length > 0 ? (
            renderCard(
              waste[waste.length - 1]!,
              'waste',
              0,
              waste.length - 1,
              true
            )
          ) : (
            <div className="solitaire-card placeholder" />
          )}
        </div>

        <div className="solitaire-spacer" />

        {/* Foundations */}
        <div className="solitaire-foundations">
          {foundations.map((foundation, i) => (
            <div key={i} className="solitaire-foundation" onClick={(e) => e.stopPropagation()}>
              {foundation.length > 0 ? (
                renderCard(
                  foundation[foundation.length - 1]!,
                  'foundation',
                  i,
                  foundation.length - 1,
                  true
                )
              ) : (
                renderFoundationPlaceholder(i)
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tableau */}
      <div className="solitaire-tableau">
        {tableau.map((column, colIndex) => (
          <div
            key={colIndex}
            className="solitaire-column"
            onClick={(e) => {
              e.stopPropagation()
              if (column.length === 0) {
                handleEmptyTableauClick(colIndex)
              }
            }}
          >
            {column.length === 0 ? (
              <div className="solitaire-card placeholder tableau-placeholder" />
            ) : (
              column.map((card, cardIndex) =>
                renderCard(
                  card,
                  'tableau',
                  colIndex,
                  cardIndex,
                  cardIndex === column.length - 1
                )
              )
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
